/**
 * AdminSidebarEditor — lets system admins rename, hide, and reorder every
 * item in the left admin navigation (AdminLayout SECTIONS). Changes are
 * stored server-side via /api/admin/admin-sidebar-config and applied to
 * AdminLayout at mount.
 *
 * Data shape: { overrides: { [path]: { label?: string, hidden?: bool } },
 *               order: [path,...]  // global ordering across sections }
 */
import { useEffect, useMemo, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Settings, Save, RotateCcw, Eye, EyeOff, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import { ADMIN_SIDEBAR_SECTIONS } from './adminSidebarSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSidebarEditor() {
  const { authFetch } = useAdmin();
  const [overrides, setOverrides] = useState({});
  const [order, setOrder] = useState([]); // per-section ordering of paths
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${API}/admin/admin-sidebar-config`);
        if (r.ok) {
          const d = await r.json();
          setOverrides(d.overrides || {});
          setOrder(d.order || []);
        }
      } catch {}
    })();
  }, [authFetch]);

  const sections = useMemo(() => {
    // Apply the current order to each section's items (local, not saved).
    const idx = new Map(order.map((p, i) => [p, i]));
    return ADMIN_SIDEBAR_SECTIONS.map(s => ({
      ...s,
      items: [...s.items].sort((a, b) => {
        const ai = idx.has(a.to) ? idx.get(a.to) : 1e6;
        const bi = idx.has(b.to) ? idx.get(b.to) : 1e6;
        return ai - bi;
      }),
    }));
  }, [order]);

  const setOverride = (path, patch) => {
    setOverrides(o => ({ ...o, [path]: { ...(o[path] || {}), ...patch } }));
    setDirty(true);
  };

  const moveItem = (sectionKey, idx, dir) => {
    const section = sections.find(s => s.key === sectionKey);
    if (!section) return;
    const target = idx + dir;
    if (target < 0 || target >= section.items.length) return;
    const newItems = [...section.items];
    [newItems[idx], newItems[target]] = [newItems[target], newItems[idx]];
    // Rebuild global order: merge new section ordering into previous order.
    const otherPaths = order.filter(p => !newItems.find(i => i.to === p));
    setOrder([...otherPaths, ...newItems.map(i => i.to)]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/admin/admin-sidebar-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides, order }),
      });
      if (r.ok) {
        toast.success('Spremljeno — osvježite stranicu da vidite izmjene u lijevom izborniku.');
        setDirty(false);
      } else {
        toast.error('Greška pri spremanju');
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!window.confirm('Poništiti sve izmjene izbornika?')) return;
    setOverrides({});
    setOrder([]);
    setDirty(true);
  };

  const q = query.toLowerCase();

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="admin-sidebar-editor">
      <div className="flex items-center gap-3 mb-2">
        <Settings size={22} className="text-red-500" />
        <h1 className="text-2xl font-bold">Uredi admin izbornik</h1>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Preimenujte, sakrijte ili premjestite stavke u lijevom admin izborniku. Izmjene su globalne za sve admine.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4 sticky top-0 bg-zinc-950 z-10 py-2">
        <label className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={query} onChange={e => setQuery(e.target.value)}
                 placeholder="Pretraži stavku…"
                 data-testid="sidebar-editor-search"
                 className="bg-zinc-900 border border-zinc-800 rounded pl-7 pr-2 py-1.5 text-sm w-64" />
        </label>
        <button onClick={save} disabled={!dirty || saving}
                data-testid="sidebar-editor-save"
                className="ml-auto inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white px-4 py-1.5 rounded text-sm font-bold">
          <Save size={14} /> Spremi
        </button>
        <button onClick={reset} data-testid="sidebar-editor-reset"
                className="inline-flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 rounded text-sm">
          <RotateCcw size={14} /> Poništi sve
        </button>
      </div>

      <div className="space-y-5">
        {sections.map(section => {
          const visible = section.items.filter(it =>
            !q || it.label.toLowerCase().includes(q) || it.to.toLowerCase().includes(q),
          );
          if (!visible.length) return null;
          return (
            <div key={section.key} className="border border-zinc-800 rounded-lg overflow-hidden"
                 data-testid={`sidebar-editor-section-${section.key}`}>
              <div className="bg-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-zinc-400">
                {section.label} · {visible.length}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {visible.map((it, i) => {
                    const ov = overrides[it.to] || {};
                    const currentLabel = ov.label ?? it.label;
                    const hidden = !!ov.hidden;
                    const absoluteIdx = section.items.findIndex(x => x.to === it.to);
                    return (
                      <tr key={it.to} className="border-t border-zinc-800" data-testid={`sidebar-item-${it.to}`}>
                        <td className="px-2 py-1 w-14 text-center">
                          <button onClick={() => moveItem(section.key, absoluteIdx, -1)}
                                  className="p-0.5 text-zinc-500 hover:text-zinc-100"
                                  data-testid={`sidebar-up-${it.to}`}><ArrowUp size={12} /></button>
                          <button onClick={() => moveItem(section.key, absoluteIdx, 1)}
                                  className="p-0.5 text-zinc-500 hover:text-zinc-100"
                                  data-testid={`sidebar-down-${it.to}`}><ArrowDown size={12} /></button>
                        </td>
                        <td className="px-3 py-1 w-64 text-xs text-zinc-500 font-mono">{it.to}</td>
                        <td className="px-3 py-1">
                          <input
                            value={currentLabel}
                            onChange={e => setOverride(it.to, { label: e.target.value })}
                            data-testid={`sidebar-label-${it.to}`}
                            className={`bg-transparent border-b border-zinc-800 focus:border-red-500 outline-none w-full ${hidden ? 'text-zinc-600 line-through' : 'text-zinc-100'}`}
                          />
                          {ov.label && ov.label !== it.label && (
                            <span className="text-[10px] text-amber-400 ml-1" title={`Original: ${it.label}`}>
                              (preimenovano)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1 w-20 text-center">
                          <button
                            onClick={() => setOverride(it.to, { hidden: !hidden })}
                            data-testid={`sidebar-toggle-${it.to}`}
                            className={`p-1 rounded ${hidden ? 'text-zinc-600' : 'text-emerald-400'}`}
                            title={hidden ? 'Skriveno — klikni da prikažeš' : 'Vidljivo — klikni da sakriješ'}>
                            {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
